'use client';

import GloLayout from 'code-micro/common/GloLayoutA';
import { ContentServerProps } from 'code-micro/props/content';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useEffect, useRef, useState } from 'react';
import { Swiper as SwiperType } from 'swiper/types';
import './index.scss';
import 'swiper/scss';
import EcoImg from 'eco-design/base/eco-img';
import EcoLink from 'eco-design/base/eco-link';
import { ItfProductCategoryData, ItfProductCategoryItem } from '../setting/type';
import { handle_GTM_80 } from '@gtm/80';
import { handle_GTM_52 } from '@gtm/52';

export default function Content({ id, data = {}, isEditStatus }: ContentServerProps) {
    const swiperRef = useRef<SwiperType>(null);
    const isClick = useRef<boolean>(false);
    const isClickTimer = useRef<NodeJS.Timeout | null>(null);
    const { theme, items = [] as ItfProductCategoryItem[] } = data as ItfProductCategoryData;
    const prefixCls = 'product-category';
    const [active, setActive] = useState<string>('');
    const [isFixed, setIsFixed] = useState<boolean>(false);
    const [top, setTop] = useState<number>(0);
    const [css, setCss] = useState({ left: 120, width: 120, opacity: 0 });
    const observerRef = useRef<MutationObserver | null>(null);

    const watchCSSVariable = (variableName: string, callback: (cur: any, prev: any) => void, element = document.documentElement) => {
        let lastValue = getComputedStyle(element).getPropertyValue(variableName).trim() || 0;
        const observer = new MutationObserver(() => {
            const currentValue = getComputedStyle(element).getPropertyValue(variableName).trim();

            if (currentValue !== lastValue) {
                callback(currentValue, lastValue);
                lastValue = currentValue;
            }
        });
        observer.observe(element, {
            attributes: true,
            attributeFilter: ['style'], // 只监听 style 属性
        });
        return observer;
    };

    const listenScroll = () => {
        const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
        if (scrollTop <= top) {
            return setIsFixed(false);
        }
        if (scrollTop > top + 80) {
            return setIsFixed(true);
        }
    };

    const listenScrollCss = (anchor: string) => {
        setActive(anchor);
        if (anchor === '') {
            setCss((prev: any) => ({
                ...prev,
                opacity: 0,
            }));
            return;
        }

        // swiper 动画
        if (swiperRef.current) {
            const swiper = swiperRef.current;
            const index = items.findIndex((i) => anchor === i.anchor);
            // @ts-ignore
            const wrapperWidth = swiper.virtualSize; // Swiper 内部计算的总宽度
            const slide = swiper.slides?.[index] as HTMLDivElement;
            const slideWidth = slide?.offsetWidth;
            const slideLeft = slide?.offsetLeft;
            const swiperWidth = swiper?.width;
            // 原始目标位置：让 slide 居中
            let target = slideLeft - swiperWidth / 2 + slideWidth / 2;
            // 👉 做边界处理
            const minTranslate = 0; // 不允许向右移动超过起点
            const maxTranslate = wrapperWidth - swiperWidth; // 不允许向左移动超过终点
            // 夹紧偏移量（防止空白）
            target = Math.min(Math.max(target, minTranslate), maxTranslate);
            swiper.slideTo(index, 300);
            swiper.translateTo(-target, 300);
        }

        // 选中条动画
        const targetItem: HTMLAnchorElement | null = document.querySelector(
            `.editor-${prefixCls}-${id} .product-category-swiper-slide-item[href="${
                anchor?.startsWith('#') ? `#editor-${anchor.slice(1)}}` : `#editor-${anchor}`
            }"]`,
        );
        const wrapper = document.querySelector(`.editor-${prefixCls}-${id} .swiper-wrapper`);
        if (targetItem && wrapper) {
            const recc = wrapper.getBoundingClientRect();
            const rect = targetItem.getBoundingClientRect();
            setCss({
                opacity: 1,
                width: rect.width,
                left: rect.left - recc.left,
            });
        }
    };

    const observeEnter = () => {
        const observers: IntersectionObserver[] = [];
        items
            ?.filter((item: ItfProductCategoryItem) => item.type === 'anchor')
            ?.forEach((item: ItfProductCategoryItem) => {
                const element: HTMLElement | null = document.getElementById(`editor-${item.anchor?.startsWith('#') ? item.anchor.slice(1) : `${item.anchor}`}`);
                if (element) {
                    const observer = new IntersectionObserver(
                        (entries) => {
                            entries.forEach((entry) => {
                                if (entry.isIntersecting) {
                                    if (isClick.current) return;
                                    listenScrollCss(item.anchor);
                                } else {
                                    listenScrollCss('');
                                }
                            });
                        },
                        {
                            root: null, // 观察视窗
                            rootMargin: '20% 0px 20% 0px', // 只观察中间60%区域
                            threshold: 0.2, // 进入视窗20%时触发
                        },
                    );
                    observer.observe(element);
                    observers.push(observer);
                }
            });
        return () => {
            observers.forEach((observer) => observer.disconnect());
        };
    };

    const handle_GTM_Event = (item: ItfProductCategoryItem) => {
        switch (item.type) {
            case 'anchor':
                handle_GTM_80({
                    position: item.name,
                    button_name: item.name,
                    __btn_id1: `editor-${prefixCls}-${id}`,
                    __btn_id2: item?.anchor?.startsWith('#') ? `#editor-${item?.anchor.slice(1)}` : `#editor-${item?.anchor}`,
                    __btn_type: 'href',
                });
                break;
            case 'link':
                handle_GTM_52({
                    list_name: 'Page Navigation',
                    product_name: item.name,
                    button_name: item.name,
                    position: 'Series_pagenavigation',
                    __btn_id1: `editor-${prefixCls}-${id}`,
                    __btn_id2: item.anchor,
                    __btn_type: 'href',
                });
                break;
            default:
                break;
        }
    };

    // 处理锚点点击事件 部分dom被遮挡的问题
    const handleClick = (item: ItfProductCategoryItem) => {
        // 点击情况下，阻止滚动判断执行
        if (isClickTimer.current !== null) {
            clearTimeout(isClickTimer.current);
            isClickTimer.current = null;
        }
        isClick.current = true;

        handle_GTM_Event(item);
        if (item.type === 'link') return;
        const dom: HTMLElement | null = document.getElementById(`editor-${item.anchor?.startsWith('#') ? item.anchor?.slice(1) : item.anchor}`);
        const header: HTMLDivElement | null = document?.querySelector('.ecoflow-frame-header');
        const naver: HTMLDivElement | null = document.querySelector('.editor-product-category');
        if (dom) {
            const adjust = window.innerWidth > 1440 ? 160 : window.innerWidth > 1024 ? 80 : 60;
            let y = 0;
            const rect = dom.getBoundingClientRect();
            y += rect.top + window.scrollY || window.pageYOffset;
            let headerH = 0;
            if (header) {
                const styles = window.getComputedStyle(header);
                const position = styles.position;
                headerH = position === 'sticky' ? header?.offsetHeight || 0 : 0;
            }
            y = y - headerH - (naver?.offsetHeight || 0) + adjust;
            window.scrollTo({
                top: y,
                behavior: 'smooth',
            });
        }
        listenScrollCss(item.anchor);

        isClickTimer.current = setTimeout(() => {
            isClick.current = false;
        }, 1000);
    };

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const observer = watchCSSVariable('--header-height', (newValue, oldValue) => {
                console.log(`CSS 变量变化: ${oldValue} -> ${newValue}`);
                let h: number = 0;
                const header: HTMLHeadingElement | null = document.querySelector('.ecoflow-frame-header') || document.querySelector(`.global-header`);
                if (header) {
                    const styles = window.getComputedStyle(header);
                    const position = styles.position;
                    h += position === 'sticky' ? parseFloat(newValue) : 0 || 0;
                }
                setTop(h);
            });
            observerRef.current = observer;
            return () => {
                if (observerRef.current) {
                    observerRef.current.disconnect();
                    observerRef.current = null;
                }
            };
        }
    }, []);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.addEventListener('scroll', listenScroll);
            return () => {
                window.removeEventListener('scroll', listenScroll);
            };
        }
    }, [top]);

    useEffect(() => {
        if (typeof window !== 'undefined' && items?.length > 0) {
            const cleanup = observeEnter();
            return cleanup;
        }
    }, [items]);

    return (
        <>
            <GloLayout
                className={`editor-${prefixCls} editor-${prefixCls}-${id} theme-${theme} sticky-${isFixed ? 'yes' : 'no'}`}
                style={isEditStatus ? { zIndex: 90 } : { top: `${top}px` }}
                id={id}
                data={data}
                floorName={prefixCls}
            >
                <Swiper
                    className={`${prefixCls}-swiper ${css?.opacity ? 'show-bar' : 'hide-bar'}`}
                    initialSlide={0}
                    spaceBetween={0}
                    slidesPerView={'auto'}
                    // @ts-ignore
                    onSwiper={(swiper) => (swiperRef.current = swiper)}
                    // @ts-ignore
                    style={{ '--left': `${css.left}px`, '--width': `${css.width}px`, '--opacity': css.opacity }}
                >
                    {items?.map((item: ItfProductCategoryItem, k: number) => (
                        <SwiperSlide
                            className={`${prefixCls}-swiper-slide  ${item.anchor === active && isFixed ? ` active` : ''}`}
                            data-anchor={item.anchor}
                            key={k}
                        >
                            <EcoLink
                                className={`${prefixCls}-swiper-slide-item GtmModuleData-btn-href`}
                                preventDefault={item?.type === 'anchor'}
                                href={
                                    item.type === 'link'
                                        ? item.anchor
                                        : item.anchor?.startsWith('#')
                                        ? `#editor-${item.anchor.slice(1)}}`
                                        : `#editor-${item.anchor}`
                                }
                                style={{ scrollMarginTop: `80px` }}
                                onClick={() => handleClick(item)}
                            >
                                <div className={`${prefixCls}-swiper-slide-item-img`}>
                                    <EcoImg pc={item.img} alt={item.name} aria-label={item.name} />
                                </div>
                                <p className={`${prefixCls}-swiper-slide-item-name`} title={item?.name}>
                                    {item.name}
                                </p>
                            </EcoLink>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </GloLayout>
        </>
    );
}
